import { Injectable } from '@angular/core';
import { compact, isEmpty, isUndefined, startsWith } from 'lodash';
import { BannerCanvasTypesEnum } from '../features-data/features-data.data';
import { IRouter } from '../shared/interfaces/router.interface';
import { UrlService } from '../shared/services/url/url.service';
import { IDeepLinkProperties } from './deep-link-properties.interface';
import { DeepLinkTriggerActionsEnum, DeepLinkTypeEnum } from './deep-link.enum';
import { SCREEN_NAMES_TO_ROUTES } from './screen-names-to-routes.constant';
import { SCREEN_NAMES_TO_ACTION } from './screen-names-to-action.constant';
import { ScreenNamesEnum } from './screen-names.enum';
import { Location } from '@angular/common';
import { environment } from '../../../environments/environment';

export const I2G_DEEP_LINK_PREFIX = 'i2g://';
export const I2G_INTERNAL_DEEP_LINK_PREFIX = 'i2gi://'; // secure deep link

@Injectable()
export class NavigationBuilder {
  constructor(private _urlService: UrlService) {}

  buildDeepLinkUrl(
    deepLinkType: DeepLinkTypeEnum,
    screenName: ScreenNamesEnum,
    triggerAction: DeepLinkTriggerActionsEnum,
    params?: { [param: string]: string }
  ): string {
    const prefix =
      deepLinkType === DeepLinkTypeEnum.internal
        ? I2G_DEEP_LINK_PREFIX
        : `${environment.redirectServiceUrl}/`;
    const deepLinkUrl = Location.joinWithSlash(
      `${prefix}${triggerAction}`,
      screenName
    );
    const queryParams = this._urlService.serializeParams(params);

    return `${deepLinkUrl}${Location.normalizeQueryParams(queryParams)}`;
  }

  buildDeepLinkProperties(deepLink: string): IDeepLinkProperties {
    // parsing empty url to 'no_action' so it handles empty urls
    const parsedDeepLink = isEmpty(deepLink) ? 'i2g://no_action' : deepLink;

    if (parsedDeepLink.match(/^https?:\/\//)) {
      return {
        triggerAction: DeepLinkTriggerActionsEnum.external_uri,
        path: parsedDeepLink,
      };
    }

    if (
      parsedDeepLink.indexOf(I2G_DEEP_LINK_PREFIX) !== 0 &&
      parsedDeepLink.indexOf(I2G_INTERNAL_DEEP_LINK_PREFIX) !== 0
    ) {
      throw new Error(
        `invalid deep link protocol provided. provided deep link ${parsedDeepLink}`
      );
    }

    const isInternalDeepLink =
      parsedDeepLink.indexOf(I2G_INTERNAL_DEEP_LINK_PREFIX) > -1;
    const url = isInternalDeepLink
      ? this._urlService.parse(
          '/' + parsedDeepLink.slice(I2G_INTERNAL_DEEP_LINK_PREFIX.length)
        )
      : this._urlService.parse(
          '/' + parsedDeepLink.slice(I2G_DEEP_LINK_PREFIX.length)
        );
    const path = compact(url.path.split('/'));
    const triggerAction = path[0];

    if (isUndefined(triggerAction)) {
      throw new Error(
        `invalid trigger action provided. was provided: ${triggerAction}`
      );
    }

    return {
      triggerAction: triggerAction as DeepLinkTriggerActionsEnum,
      path: path.slice(1).join(''),
      params: url.query,
      internal: isInternalDeepLink,
    };
  }

  buildRouteFromDeepLinkProperties(
    deepLinkProperties: IDeepLinkProperties
  ): IRouter {
    let screenNameToRoute: (deepLinkProperties: IDeepLinkProperties) => IRouter;

    switch (deepLinkProperties.triggerAction) {
      case DeepLinkTriggerActionsEnum.paywall:
        screenNameToRoute = SCREEN_NAMES_TO_ROUTES[ScreenNamesEnum.paywall];
        break;
      case DeepLinkTriggerActionsEnum.purchase:
        screenNameToRoute = SCREEN_NAMES_TO_ROUTES[ScreenNamesEnum.purchase];
        break;
      case DeepLinkTriggerActionsEnum.navigate:
        screenNameToRoute = SCREEN_NAMES_TO_ROUTES[deepLinkProperties.path];
        break;
      case DeepLinkTriggerActionsEnum.home:
        screenNameToRoute = SCREEN_NAMES_TO_ROUTES[ScreenNamesEnum.home];
        break;
      case DeepLinkTriggerActionsEnum.external_uri: // handle external uris
        return {
          route: [deepLinkProperties.path],
          external: true,
        };
      case DeepLinkTriggerActionsEnum.action:
      // fall through
      default:
        return null;
    }

    if (!isUndefined(screenNameToRoute)) {
      return screenNameToRoute(deepLinkProperties);
    } else {
      return {
        route: ['/'],
      };
    }
  }

  buildActionFromDeeplinkProperties(
    deepLinkProperties: IDeepLinkProperties
  ): any {
    let screenNameToAction: (deepLinkProperties: IDeepLinkProperties) => any;

    switch (deepLinkProperties.triggerAction) {
      case DeepLinkTriggerActionsEnum.action:
      case DeepLinkTriggerActionsEnum.navigate: {
        screenNameToAction = SCREEN_NAMES_TO_ACTION[deepLinkProperties.path];
        break;
      }
      default:
        return null;
    }

    if (!isUndefined(screenNameToAction)) {
      return screenNameToAction(deepLinkProperties);
    } else {
      return null;
    }
  }

  buildGrowthDialogDataFromDeepLinkProperties(
    deepLinkProperties: IDeepLinkProperties
  ): { canvasType: BannerCanvasTypesEnum; featureName: string } {
    const canvasName = deepLinkProperties.path;
    const bannerCanvasTypeEnums: BannerCanvasTypesEnum[] = [
      BannerCanvasTypesEnum.window,
      BannerCanvasTypesEnum.feature_lock,
      BannerCanvasTypesEnum.feature_highlight,
      BannerCanvasTypesEnum.quota_reached,
    ];
    let selectedCanvasType: BannerCanvasTypesEnum;
    let featureName: string;

    for (let i = 0; i < bannerCanvasTypeEnums.length; i++) {
      const cType = bannerCanvasTypeEnums[i];

      if (startsWith(canvasName, cType)) {
        selectedCanvasType = cType;
        break;
      }
    }

    if (!isUndefined(selectedCanvasType)) {
      featureName = canvasName.substring(selectedCanvasType.length + 1); // accounts for the
      // `_` after the
      // canvas type
    } else {
      featureName = canvasName;
    }

    return {
      canvasType: selectedCanvasType,
      featureName,
    };
  }
}
